import React from 'react';
import { useTranslations } from 'next-intl';
import { useAppSelector } from '@/hooks/redux';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import {
  getDownloadSignupErrorMessage,
  isValidDownloadEmail,
  useDownloadEmailSignup,
} from '@/features/tool/hooks/useDownloadEmailSignup';
import ProtectPdfDialog from './ProtectPdfDialog';
import ProtectPdfField from './ProtectPdfField';
import ProtectPdfButton from './ProtectPdfButton';

export type ProtectPdfProtectedModalData = {
  toolType?: string;
  originEntryPoint?: string;
  extension?: string;
  onDownloadClick: () => void | Promise<void>;
  onContinue: () => void;
};

const ProtectPdfProtectedModal = ({
  data,
  hideModal,
}: {
  data?: ProtectPdfProtectedModalData;
  hideModal: () => void;
}) => {
  const t = useTranslations('protect_pdf_flow');
  const user = useAppSelector((state) => state.auth.user);
  const [isGuest] = React.useState(() => !user);
  const { submitEmail } = useDownloadEmailSignup();
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const isSubmittingRef = React.useRef(false);
  const analyticsSource = React.useMemo(
    () =>
      buildStructuredAnalyticsSource(data?.toolType, data?.originEntryPoint),
    [data?.originEntryPoint, data?.toolType]
  );

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!isGuest) {
      return;
    }

    logHubxEvent({
      eventName: APP_EVENTS.LND_EMAIL,
      source: analyticsSource.toolType,
      event_params: {
        source: analyticsSource,
      },
    });
  }, [analyticsSource, isGuest]);

  const handleClose = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    hideModal();
  }, [hideModal]);

  const proceedToDownload = React.useCallback(() => {
    hideModal();
    data?.onContinue();
  }, [data, hideModal]);

  const submit = React.useCallback(async () => {
    if (isSubmittingRef.current || !data) {
      return;
    }

    if (!isGuest) {
      isSubmittingRef.current = true;
      await Promise.resolve(data.onDownloadClick()).catch(() => undefined);
      isSubmittingRef.current = false;
      proceedToDownload();
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidDownloadEmail(normalizedEmail)) {
      setEmailError(t('invalid_email_error'));
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setEmailError(null);
    setEmail(normalizedEmail);

    await Promise.resolve(data.onDownloadClick()).catch(() => undefined);

    logHubxEvent({
      eventName: APP_EVENTS.BTN_DOWNLOAD_EMAIL,
      source: analyticsSource.toolType,
      event_params: {
        source: analyticsSource,
      },
    });

    try {
      const result = await submitEmail(normalizedEmail, {
        extension: data.extension,
        toolType: data.toolType,
        originEntryPoint: data.originEntryPoint,
        onSuccess: data.onContinue,
      });

      isSubmittingRef.current = false;

      if (result === 'registered') {
        proceedToDownload();
      }
    } catch (signupError) {
      isSubmittingRef.current = false;

      if (isMountedRef.current) {
        setEmailError(getDownloadSignupErrorMessage(signupError));
        setIsSubmitting(false);
      }
    }
  }, [
    analyticsSource,
    data,
    email,
    isGuest,
    proceedToDownload,
    submitEmail,
    t,
  ]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return (
    <ProtectPdfDialog
      name="ProtectPdfProtected"
      tone="success"
      title={t('protected_title')}
      description={t('protected_description')}
      descriptionClassName="!leading-[24px]"
      onDismiss={handleClose}
    >
      <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-8">
        {isGuest ? (
          <ProtectPdfField
            id="protect-pdf-download-email"
            type="email"
            inputMode="email"
            label={t('email_label')}
            placeholder={t('email_placeholder')}
            value={email}
            onChange={(value) => {
              setEmail(value);

              if (emailError) {
                setEmailError(null);
              }
            }}
            autoComplete="email"
            errorMessage={emailError}
            disabled={isSubmitting}
          />
        ) : null}

        <div className="flex flex-col gap-2">
          <ProtectPdfButton type="submit" isLoading={isSubmitting}>
            {t('download_button')}
          </ProtectPdfButton>
          <ProtectPdfButton
            variant="secondary"
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            {t('close_button')}
          </ProtectPdfButton>
        </div>
      </form>
    </ProtectPdfDialog>
  );
};

export default ProtectPdfProtectedModal;
